import { create } from 'zustand'
import api from '../utils/api'

/**
 * Manages native browser Notification API permissions and Web Push subscriptions.
 *
 * Flow:
 *   1. requestPermission() — prompts the user for notification permission
 *   2. subscribeToPush()   — subscribes to PushManager with VAPID key and saves to backend
 *   3. showNotification()  — fires a native desktop notification (works even when app is backgrounded)
 */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

const useNotifications = create((set, get) => ({
  permission: typeof Notification !== 'undefined' ? Notification.permission : 'denied',
  pushSubscription: null,
  loading: false,
  supported: typeof Notification !== 'undefined' && 'serviceWorker' in navigator,

  /**
   * Request notification permission from the browser.
   * Returns the resulting permission state.
   */
  requestPermission: async () => {
    if (typeof Notification === 'undefined') return 'denied'

    set({ loading: true })
    try {
      const result = await Notification.requestPermission()
      set({ permission: result, loading: false })

      // Auto-subscribe to push if granted
      if (result === 'granted') {
        await get().subscribeToPush()
      }

      return result
    } catch (err) {
      console.error('[Notifications] Permission request failed:', err)
      set({ loading: false })
      return 'denied'
    }
  },

  /**
   * Subscribe to Web Push via the service worker's PushManager.
   * Saves the subscription to the backend for server-side push delivery.
   */
  subscribeToPush: async () => {
    try {
      const registration = await navigator.serviceWorker.ready

      // Get VAPID key from backend
      const { data } = await api.get('/push/vapid-key')
      const vapidKey = urlBase64ToUint8Array(data.public_key)

      // Check for existing subscription
      let subscription = await registration.pushManager.getSubscription()

      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidKey,
        })
      }

      // Save to backend
      const subJson = subscription.toJSON()
      await api.post('/push/subscribe', {
        endpoint: subJson.endpoint,
        subscription: subJson,
      })

      set({ pushSubscription: subscription })
      console.log('[Push] Subscribed and saved to backend')
    } catch (err) {
      console.error('[Push] Subscription failed:', err)
    }
  },

  /**
   * Show a native desktop notification immediately.
   * Works even when the app is in the background or another tab is focused.
   * Falls back to service worker notification if available.
   */
  showNotification: async (alertData) => {
    const { permission } = get()
    if (permission !== 'granted') return

    const { coin, condition, target, current } = alertData || {}

    let direction = 'hit your target'
    if (condition === 'above') direction = 'risen above'
    else if (condition === 'below') direction = 'fallen below'
    else if (condition === 'crosses') direction = 'crossed through'

    const title = `🚨 ${coin || 'Crypto'} Alert!`
    const body = coin
      ? `${coin} has ${direction} $${Number(target).toLocaleString()}. Current: $${Number(current).toLocaleString()}`
      : 'A price alert was triggered.'

    const options = {
      body,
      icon: '/icon-192.png',
      badge: '/badge-72x72.png',
      vibrate: [200, 100, 200, 100, 200],
      tag: `alert-${coin}-${Date.now()}`,
      requireInteraction: true,
      data: alertData,
    }

    try {
      // Prefer service worker notifications (work when app is backgrounded)
      const registration = await navigator.serviceWorker?.ready
      if (registration) {
        await registration.showNotification(title, options)
      } else {
        // Fallback to Notification constructor
        new Notification(title, options)
      }
    } catch (err) {
      console.error('[Notifications] Failed to show notification:', err)
      // Final fallback
      try {
        new Notification(title, options)
      } catch {}
    }
  },

  /**
   * Send a test push notification via the backend.
   * Returns { ok, error }.
   */
  testPush: async () => {
    try {
      const { data } = await api.post('/push/test')
      return { ok: true, sent: data.sent }
    } catch (err) {
      return { ok: false, error: err.response?.data?.detail || 'Test push failed' }
    }
  },

  /**
   * Sync permission state (call on mount to detect changes).
   */
  syncPermission: () => {
    if (typeof Notification !== 'undefined') {
      set({ permission: Notification.permission })
    }
  },
}))

export default useNotifications
